import { RequestViewingMode } from '@kijiji/generated/graphql-types'
import { useTranslation } from 'next-i18next'
import { type ComponentProps } from 'react'

import { TRANSLATION_KEYS } from '@/constants/localization'
import { RequestViewingForm } from '@/features/listing/components/request-viewing/RequestViewForm/RequestViewingForm'
import { RequestViewingSubscriptionForm } from '@/features/listing/components/request-viewing/RequestViewForm/RequestViewingSubscriptionForm'
import { ModalFlexFormContainer } from '@/features/listing/components/request-viewing/styled'
import { useRequestViewingState } from '@/features/listing/hooks/request-viewing/useRequestViewingState'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { trackEvent } from '@/lib/ga/utils/tracking'
import { Modal } from '@/ui/molecules/modal/Modal'

type RequestViewingModalProps = Omit<
  ComponentProps<typeof Modal>,
  'label' | 'closeButtonLabel' | 'children' | 'isOpen' | 'onCancel' | 'id'
> & {
  listingId: string
  onSuccess: () => void
  onError: () => void
  source: string
}

export const RequestViewingModal = ({
  listingId,
  onSuccess,
  onError,
  source,
  ...rest
}: RequestViewingModalProps) => {
  const { t } = useTranslation(TRANSLATION_KEYS.REQUEST_VIEWING)
  const { isRequestViewingModalOpen, setRequestViewingState, requestViewingMode } =
    useRequestViewingState()

  const isSubscriptionSeller = requestViewingMode === RequestViewingMode.SubscriptionSeller

  const handleOnSuccess = () => {
    setRequestViewingState({ isOpen: false, notification: 'success' })
    onSuccess()
  }
  const handleOnError = () => {
    setRequestViewingState({ isOpen: false, notification: 'error' })
    onError()
  }

  const handleOnCancel = () => {
    setRequestViewingState({ isOpen: false })
    trackEvent({
      action: GA_EVENT.RequestViewingCancel,
      /* 
        The label is static as inherited from the legacy implementation.
        In case FAST wants to turn it dynamic, it can be done by using `apps/nwa/src/features/listing/utils/request-viewing/generateRequestViewingAttemptTrackingLabel.ts`
      */
      label: `full_name=1/0;option_1=1/0;option_2=1/0;option_3=1/0;ad_id=${listingId}`,
      customParameters: { eventCategory: source },
    })
  }

  return (
    <Modal
      isOpen={isRequestViewingModalOpen}
      id="request-viewing-modal"
      label={t('request_viewing:title')}
      closeButtonLabel={t('request_viewing:form.close_button.label')}
      onCancel={handleOnCancel}
      maxWidth={isSubscriptionSeller ? { medium: '40.2rem' } : '48rem'}
      maxHeight={isSubscriptionSeller ? { medium: '82rem' } : { large: '71.7rem' }}
      hasCloseButton
      title={t('request_viewing:title')}
      {...rest}
    >
      <ModalFlexFormContainer flexDirection="column" flexGrow={1}>
        {isSubscriptionSeller ? (
          <RequestViewingSubscriptionForm
            listingId={listingId}
            source={source}
            onSuccessFallback={handleOnSuccess}
          />
        ) : (
          <RequestViewingForm
            listingId={listingId}
            onSuccess={handleOnSuccess}
            onError={handleOnError}
            source={source}
          />
        )}
      </ModalFlexFormContainer>
    </Modal>
  )
}
